/* eslint-disable @typescript-eslint/no-explicit-any */
import _ from 'lodash';
import { RESTRICT_SPEC_REGEX } from '../constants';
import { SelectedOrgInfo, SiteInfo } from '../types/site';

import { Role, RoleLocationList, SiteRole, SiteRoleData, UserRole } from '../types/user';

export const flattenRoleLocationList = (
    roleData: Role
): Array<{
    roleId: string;
    roleName: string;
    siteId: string;
    roleDescription: string;
    operation: string[] | undefined;
}> =>
    _.flatten(
        roleData.map((role) => {
            if (role.siteId.length === 0) {
                const val = [
                    {
                        roleId: role.roleId,
                        roleName: role.roleName,
                        siteId: '',
                        roleDescription: role.roleName,
                        operation: role.operations,
                    },
                ];
                return val;
            }
            const siteRole = role.siteId.map((site) => ({
                roleId: role.roleId,
                roleName: role.roleName,
                siteId: site,
                roleDescription: role.roleName,
                operation: role.operations,
            }));
            return siteRole;
        })
    );

const flattenOrgLocationList = (
    orgLocationList: SelectedOrgInfo,
): Array<{
    id: string;
    name: string;
    address: string;
}> => {
    const locationList = orgLocationList.locations.map((x) => ({
            id: x.locationId,
            name: x.locationName,
            address: x.address
        }));
    const orgLocation =  [...locationList, ...[{
                id: orgLocationList.organizationId, name: orgLocationList.organizationName,
                address: orgLocationList.address
            }]];
        return orgLocation;
    }
    
export const bindRoleLocationReadOnlyView = (
    roleData: Role,
    editOrganizationData: SelectedOrgInfo
): SiteRoleData[] => {
    const siteArray: SiteRoleData[] = [];

    const editLocationIds = flattenOrgLocationList(editOrganizationData);

    const siteRoleInfos = _.flatten(
        roleData.map((role) => {
            const siteRole = role.siteId.map((site) => ({
                roleId: role.roleId,
                roleName: role.roleName,
                siteId: site,
            }));
            return siteRole;
        })
    );

    editLocationIds.forEach((loc) => {
        siteRoleInfos.forEach((role) => {
            if (role.siteId === loc.id) {
                const val = {
                    siteId: loc.id,
                    siteName:
                        role.roleName === UserRole.OrgAdmin
                            ? 'All Locations'
                            : role.roleName === UserRole.Unassigned
                            ? '--'
                            : loc.name,
                    siteAddress:
                        role.roleName === UserRole.OrgAdmin
                            ? ''
                            : role.roleName === UserRole.Unassigned
                            ? '--'
                            : loc.address,
                    roleName: role.roleName,
                    roleId: role.roleId,
                    operations: [],
                };
                siteArray.push(val);
            }
        });
        return siteArray;
    });
    const eatonAdminLoggedIn = roleData.length === 1 && roleData[0].roleName === UserRole.EatonAdmin;
    if (eatonAdminLoggedIn) {
        const val = {
            siteId: 'eatonAdmin',
            siteName: 'All Locations',
            siteAddress: '',
            roleName: UserRole.EatonAdmin,
            roleId: (roleData.length === 1 && roleData[0].roleId) || '',
            operations: [],
        };
        siteArray.push(val);
    }
    return siteArray;
};
export const showEditButton = (
    loggedInUserRole: UserRole,
    editUserRole: Array<{
        roleId: string;
        siteId: string[];
        roleName: string;
        roleDescription: string;
    }>,
    loggedInUserId: string,
    userId: string
): boolean => {
    switch (loggedInUserRole) {
        case UserRole.EatonAdmin:
            return editUserRole.every((role) => role.roleName !== UserRole.EatonAdmin) && loggedInUserId !== userId;
        case UserRole.OrgAdmin:
            return (
                editUserRole.every(
                    (role) => role.roleName !== UserRole.EatonAdmin && role.roleName !== UserRole.OrgAdmin
                ) && loggedInUserId !== userId
            );
        case UserRole.LocationAdmin:
            return (
                editUserRole.some(
                    (role) =>
                        role.roleName !== UserRole.EatonAdmin &&
                        role.roleName !== UserRole.OrgAdmin &&
                        role.roleName !== UserRole.LocationAdmin
                ) && loggedInUserId !== userId
            );

        case UserRole.SiteUser:
            return false;
        default:
            return false;
    }
};

export function updatedRoleList(
    roleLocationList: RoleLocationList,
    registrationRole: Array<{ roleId: string; roleName: string }>
): Array<{
    roleId: string;
    roleName: string;
}> {
    const selectedRole = roleLocationList.map((x) => x.roleId);
    const newRole = registrationRole.filter(
        (obj) => selectedRole.indexOf(obj.roleId) === -1 //eslint-disable-line @typescript-eslint/prefer-includes
    );

    const updatedRoles = newRole.filter(
        (roleInfo) =>
            roleInfo.roleName !== UserRole.OrgAdmin &&
            roleInfo.roleName !== UserRole.Unassigned &&
            roleInfo.roleName !== UserRole.EatonAdmin
    );
    return updatedRoles;
}

export function addMoreRoleLocations(
    roleLocationList: RoleLocationList,
    registrationRole: Array<{ roleId: string; roleName: string }>,
    associatedLocation?: SiteInfo[]
): RoleLocationList {
    const alreadySelectedLocation = _.flatten(roleLocationList.map((roleLoc) => roleLoc.selectedLocation));
    const disabledLoc = associatedLocation
        ? [...associatedLocation, ...alreadySelectedLocation]
        : alreadySelectedLocation;

    const add = [
        {
            index: roleLocationList.length,
            roleId: '',
            disabledLocations: disabledLoc,
            roleList: registrationRole,
            selectedLocation: [],
        },
    ];
    const newArray = [...roleLocationList, ...add];
    return newArray;
}

export function selectedAutoCompleteLocation(
    index: number,
    roleLocationList: RoleLocationList,
    selectedlocation: Array<{
        siteId: string;
        siteName: string;
    }>
): RoleLocationList {
    const addObj = [...roleLocationList];
    addObj[index].selectedLocation = selectedlocation.map((x: SiteInfo) => ({
        siteId: x.siteId,
        siteName: x.siteName,
    }));
    const otherLocationList = addObj.filter((x) => x.index !== index);

    /** set disabled value in the autocomplete */
    if (otherLocationList.length > 0)
        addObj[otherLocationList[0].index].disabledLocations = selectedlocation.map((x: SiteInfo) => ({
            siteId: x.siteId,
            siteName: x.siteName,
        }));
    return addObj;
}

export function meregeSimiliarRoles(array: any): SiteRole[] {
    const output = array.reduce((o: any, cur: any) => {
        // Get the index of the key-value pair.
        const occurs = o.reduce((n: any, item: any, i: any) => (item.roleId === cur.roleId ? i : n), -1);

        // If the name is found,
        if (occurs >= 0) {
            // append the current value to its list of values.
            o[occurs].siteIds = o[occurs].siteIds.concat(cur.siteIds);

            // Otherwise,
        } else {
            // add the current item to o (but make sure the value is an array).
            const obj = {
                roleId: cur.roleId,
                siteIds: _.isArray(cur.siteIds) ? cur.siteIds : [cur.siteIds],
            };
            /* eslint-disable no-param-reassign */
            o = o.concat([obj]);
        }

        return o;
    }, []);
    return output;
}

export function otherLocationSelectedList(index: number, addRoleLocationObj: RoleLocationList): SiteInfo[] {
    const otherLocationList = addRoleLocationObj.filter((x) => x.index !== index);

    return _.flatten(otherLocationList.map((other) => other.selectedLocation));
}
export const isValidChar = (value: string): boolean => RESTRICT_SPEC_REGEX.test(value);

export const disableLocationForEditedUser = (
    roleLocationList: SiteRoleData[],
    loggedInOrganizationData: SelectedOrgInfo,
    selectedOrg: string,
    loggedinUserRole: SiteRoleData[]
): SiteRoleData[] => {
    const loggedInUserLocationData = flattenOrgLocationList(loggedInOrganizationData);
    const loggedInUserLocationIds = loggedInUserLocationData.map((x) => x.id);
    const editLocationIds = roleLocationList.map((x) => x.siteId);
    const disableLocationIds = editLocationIds.filter((x) => !loggedInUserLocationIds.includes(x));
    const disableLocationData = roleLocationList.filter((a) => disableLocationIds.some((b) => a.siteId === b));

    const sameRoleLocation = roleLocationList.filter((y) =>
        loggedinUserRole.some((z) => z.roleId === y.roleId && z.siteId === y.siteId)
    );
    return [...disableLocationData, ...sameRoleLocation];
};
